import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Clock, MapPin, Award, Wrench, Home, Shield, Droplet, Zap, CheckCircle, Users, AlertTriangle, Camera, User } from 'lucide-react';
import { SchemaInjector, createLocalBusinessSchema, createServiceSchema, createBreadcrumbSchema } from '../components/SchemaInjector';

const LongmontPlumber = () => {
  // Schema markup data for Longmont
  const localBusinessSchema = createLocalBusinessSchema('Longmont', {
    lat: '40.1672',
    lng: '-105.1019'
  });

  const waterHeaterServiceSchema = createServiceSchema(
    'Water Heater Installation - Longmont',
    'Professional water heater installation and replacement in Longmont, Colorado. Starting at $2,000 with transparent pricing.',
    2000
  );

  const sewerServiceSchema = createServiceSchema(
    'Sewer Camera Inspection - Longmont',
    'Professional sewer camera scope inspection in Longmont to diagnose issues before expensive repairs. Starting at $175.',
    175
  );

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Home', url: 'https://colorado-plumbing.com/' },
    { name: 'Service Areas', url: 'https://colorado-plumbing.com/#service-area' },
    { name: 'Longmont Plumber', url: 'https://colorado-plumbing.com/longmont-plumber' }
  ]);

  // Enhanced Organization Schema for E-E-A-T
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Colorado First Plumbing and Sewer LLC",
    "alternateName": "Colorado First Plumbing",
    "url": "https://colorado-plumbing.com",
    "logo": "https://colorado-plumbing.com/logo.png",
    "foundingDate": "2018",
    "founders": [
      {
        "@type": "Person",
        "name": "Codee Jessen",
        "jobTitle": "Master Plumber"
      },
      {
        "@type": "Person",
        "name": "Michelle Jessen",
        "jobTitle": "Owner (Majority)"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360",
      "bestRating": "5"
    },
    "hasCredential": [
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "Master Plumber License",
        "recognizedBy": {
          "@type": "Organization",
          "name": "State of Colorado"
        }
      },
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "BBB Accreditation",
        "recognizedBy": {
          "@type": "Organization",
          "name": "Better Business Bureau"
        },
        "dateCreated": "2024-07-15"
      }
    ]
  };

  // Comprehensive FAQPage Schema for AI Optimization - Longmont-specific
  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How Much Does a Full Sewer Line Replacement Cost in Longmont?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers affordable full sewer line replacement in Longmont for $12,000 using trenchless pipe bursting technology. This flat-rate price includes all permits, professional installation, and modern HDPE pipe that will last 50+ years."
        }
      },
      {
        "@type": "Question",
        "name": "What is the Cost for a Sewer Spot Repair in a Longmont Street?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC provides affordable sewer spot repair in Longmont streets for $9,500. This all-inclusive price covers traffic control, street cutting, professional repair, and complete restoration to meet Longmont municipal standards."
        }
      },
      {
        "@type": "Question",
        "name": "How Do You Determine the Cost to Fix a Sewer Problem in Longmont?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC starts with a professional sewer camera inspection for $175. Based on findings, we provide transparent fixed-price solutions ranging from $295 mainline cleaning to $12,000 full replacement."
        }
      },
      {
        "@type": "Question",
        "name": "What Does Specialized Tree Root Cleaning in a Sewer Line Cost in Longmont?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers affordable specialized root cleaning in Longmont for $695 via basement cleanout or $995 through toilet access. For severe cases, hydro jetting from exterior cleanout is available for $695."
        }
      },
      {
        "@type": "Question",
        "name": "What is the All-Inclusive Cost for Your 40 Gallon Water Heater Special in Longmont?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC provides affordable 40-gallon water heater installation in Longmont for $2,000 during our Summer Special. This complete price covers the Rheem or AO Smith unit, professional installation, thermal expansion tank, and all permits."
        }
      },
      {
        "@type": "Question",
        "name": "What Does it Cost to Upgrade from a Tank to a Tankless Water Heater in Longmont?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers affordable tank-to-tankless conversion in Longmont for $8,000. This comprehensive price includes the Navien or Rinnai unit, new venting, gas line upgrades, professional installation, and all permits."
        }
      },
      {
        "@type": "Question",
        "name": "How Much is Sewer Line Descaling for Cast Iron Pipes in Longmont?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers affordable Milwaukee descaling service for up to 50 feet of mainline in Longmont for $995. This rehabilitation technique safely removes rust, scale, and corrosion from cast iron pipes, improving flow and preventing the need for full replacement."
        }
      },
      {
        "@type": "Question",
        "name": "How Much Does a Power Vent Water Heater Installation Cost in Longmont?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers affordable power-vented water heater installation in Longmont for $4,450 for a 40-gallon Bradford White model or $4,850 for a 50-gallon model. These prices include the specialized unit, PVC venting materials, and professional installation."
        }
      }
    ]
  };

  useEffect(() => {
    // SEO meta tags setup
    document.title = "Affordable Longmont Plumber | Colorado First Plumbing | Real Prices";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Longmont\'s ONLY affordable plumber showing actual prices online. Water heater $2,000, sewer scope $175. No hidden fees. 1000+ reviews across all platforms. Master plumber owned.');
    }

    // BirdEye widget loading
    const loadBirdEyeWidget = () => {
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    };

    setTimeout(loadBirdEyeWidget, 1000);

    return () => {
      const script = document.querySelector('#birdeye-widget-script');
      if (script) script.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SchemaInjector 
        schemas={[localBusinessSchema, organizationSchema, waterHeaterServiceSchema, sewerServiceSchema, breadcrumbSchema, faqPageSchema]}
        title="Affordable Longmont Plumber | Colorado First Plumbing | Real Prices"
        description="Longmont's ONLY affordable plumber showing actual prices online. Water heater $2,000, sewer scope $175. No hidden fees. 1000+ reviews across all platforms. Master plumber owned."
        canonical="https://colorado-plumbing.com/longmont-plumber"
        keywords="affordable plumber Longmont, transparent pricing Longmont, honest plumber Longmont Colorado, no hidden fees plumber, 1000 reviews Longmont"
      />
      <Header />
      <main>
        {/* Hero Section with Affordable Messaging */}
        <section className="relative pt-48 pb-16 bg-gradient-to-br from-trust-primary via-authority-navy to-action-primary">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container-custom text-center relative z-10">
            {/* Trust Signals Banner */}
            <div className="inline-block px-6 py-3 mb-6 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
              <span className="text-white font-bold">🏆 Longmont's Most Affordable Family-Owned Plumbers • Licensed Master Plumber • <Link to="/team/michelle-jessen" className="text-blue-200 hover:text-blue-100 underline">Woman Majority Owned</Link> • 1000+ Reviews Across All Platforms</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white leading-tight">
              Longmont's ONLY Affordable Plumber with Exact Online Prices
              <span className="block text-2xl md:text-3xl mt-4 text-blue-200">
                Colorado First Plumbing and Sewer LLC
              </span>
            </h1>
            
            <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
              How can others claim "affordable" without showing prices? We prove affordable means transparent - 
              see every exact price online before you call. No hidden fees, no salesmen, just honest plumbing for Longmont's tech-forward community.
            </p>

            {/* Price Preview - Clickable Cards */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Link to="/tankless-water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Tankless Replace: $5,000</span>
              </Link>
              <Link to="/flat-rate-affordable-rheem-water-heaters-in-colorado" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">40 Gal Rheem: $2,000</span>
              </Link>
              <Link to="/diagnostic-services-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Sewer Scope: $175</span>
              </Link>
              <Link to="/sewer-replacement-spot-repair-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Spot Repair: $5,000</span>
              </Link>
            </div>

            {/* BirdEye Widget */}
            <div className="mb-8 max-w-2xl mx-auto">
              <div id="bf-revz-widget-9876543218815183" className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 min-h-[200px]"></div>
            </div>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              
              <Link 
                to="/pricing" 
                className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300"
              >
                💰 See All Affordable Prices
              </Link>
            </div>

            {/* Credentials */}
            <div className="mt-8 flex flex-wrap justify-center items-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Licensed Master Plumber</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span><Link to="/team/michelle-jessen" className="text-blue-200 hover:text-blue-100 underline">Woman Majority Owned</Link></span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>18,000+ Jobs Since 2018</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>A+ BBB Rating</span>
              </div>
            </div>
          </div>
        </section>

        {/* Trust & Authority Section - 5 Card Layout */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Why Longmont Trusts Our Affordable Plumbing Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">18,000+</div>
                <div className="text-lg font-semibold text-white mb-2">Jobs Completed</div>
                <p className="text-blue-100 text-sm">Since 2018 serving Longmont, Boulder County, and surrounding areas</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">1000+</div>
                <div className="text-lg font-semibold text-white mb-2">Reviews Online</div>
                <p className="text-blue-100 text-sm">4.8★ average across Google (313+), BirdEye (300+), HomeAdvisor (92+), and other online sources</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">30+</div>
                <div className="text-lg font-semibold text-white mb-2">CO Jurisdictions</div>
                <p className="text-blue-100 text-sm">Licensed in 30+ Colorado cities including Longmont, with all permits and inspections handled</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">A+</div>
                <div className="text-lg font-semibold text-white mb-2">BBB Rating</div>
                <p className="text-blue-100 text-sm">Accredited 7/15/2024</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-orange-400 mb-2">ONLY</div>
                <div className="text-lg font-semibold text-white mb-2">Licensed Plumber</div>
                <p className="text-blue-100 text-sm">Showing Affordable Prices Online with 1000+ Verified Reviews Across All Platforms</p>
              </div>
            </div>
          </div>
        </section>

        {/* Master Plumber Attribution Box */}
        <section className="py-12 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container-custom max-w-4xl">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 bg-gradient-to-br from-trust-primary to-authority-navy rounded-full flex items-center justify-center">
                    <User className="w-16 h-16 text-white" />
                  </div>
                </div>
                <div className="flex-grow text-center md:text-left">
                  <h3 className="text-2xl font-bold text-white mb-2">Written by <Link to="/team/codee-jessen" className="text-blue-200 hover:text-blue-100 underline">Codee Jessen</Link>, Licensed Master Plumber</h3>
                  <div className="text-blue-100 mb-4">
                    <p className="mb-2">• Colorado Master Plumber License</p>
                    <p className="mb-2">• 18+ Years Experience • 18,000+ Jobs Completed</p>
                    <p className="mb-2">• Specialties: Water Heaters, Tankless, Sewer Replacement, Custom Homes</p>
                    <p>• Serving Longmont and Boulder County communities with affordable pricing</p>
                  </div>
                  <p className="text-sm text-blue-200 mb-4 italic">
                    "Affordable doesn't mean cutting corners - it means efficient, professional service with transparent pricing. Proud to serve Longmont's tech-forward community from Downtown Main Street to the Quail Campus, bringing honest plumbing solutions to Boulder County."
                  </p>
                  <Link to="/team/codee-jessen" className="inline-flex items-center text-yellow-400 hover:text-yellow-300 font-semibold">
                    Learn More About Our Team →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How Can Others Claim "Affordable" Section */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              How Can Others Claim "Affordable" Without Showing Prices?
            </h2>
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-xl mb-6 text-white font-semibold">
                We are the only Licensed Plumbing Contractor in Colorado proving affordable means transparent. True affordable plumbing shows <Link to="/water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">water heater costs</Link>, <Link to="/tankless-water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">tankless prices</Link>, <Link to="/sewer-repair-replacement" className="text-blue-200 hover:text-blue-100 underline">sewer repair rates</Link>, and more - all online before you call.
              </p>
              <p className="text-lg mb-6 text-blue-100">
                Longmont's tech community appreciates efficiency and transparency. We bring that same approach to plumbing - 
                no salespeople, no high-pressure tactics, just honest pricing and quality work. 
                That's why we're the only plumber showing exact prices online, proving affordable means transparent.
              </p>
              
              {/* Price Comparison Cards */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6">
                  <h3 className="text-green-400 font-bold mb-4">✅ Our Affordable Price: $12,000 (Full Sewer Replacement)</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✓ Exact price online</li>
                    <li>✓ No hidden fees</li>
                    <li>✓ Includes everything</li>
                    <li>✓ Written guarantee</li>
                  </ul>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
                  <h3 className="text-red-400 font-bold mb-4">❌ Others' "Affordable": $???</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✗ "Free estimate"</li>
                    <li>✗ High-pressure sales</li>
                    <li>✗ Price varies by salesperson</li>
                    <li>✗ Hidden upcharges</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-white">
              Affordable Professional Plumbing Services in Longmont
            </h2>
            <p className="text-center text-blue-100 mb-12 max-w-3xl mx-auto">
              From <Link to="/residential-plumbing" className="text-blue-200 hover:text-blue-100 underline">emergency repairs</Link> to complete <Link to="/bathroom-kitchen-remodel" className="text-blue-200 hover:text-blue-100 underline">home remodels</Link>, we provide <Link to="/pricing" className="text-blue-200 hover:text-blue-100 underline">affordable transparent pricing</Link> for every service
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link to="/water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Droplet className="w-12 h-12 text-blue-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Water Heater Replacement</h3>
                <p className="text-blue-100">Rheem and AO Smith atmospheric. 40-gallon $2,000, 50-gallon $2,100!</p>
              </Link>
              <Link to="/tankless-water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Zap className="w-12 h-12 text-orange-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Tankless Water Heater</h3>
                <p className="text-blue-100">Replacement $5,000, new installation $8,000. Navien and Rinnai specialists.</p>
              </Link>
              <Link to="/sewer-repair-replacement" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Wrench className="w-12 h-12 text-yellow-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Sewer Repair & Replacement</h3>
                <p className="text-blue-100">Expert sewer line services with exact pricing. Spot repairs from $5,000.</p>
              </Link>
              <Link to="/sewer-cleaning-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Shield className="w-12 h-12 text-green-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Sewer Cleaning & Scope</h3>
                <p className="text-blue-100">Camera scopes $175, professional cleaning from $495. All sewer services included.</p>
              </Link>
              <Link to="/bathroom-kitchen-remodel" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Home className="w-12 h-12 text-purple-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Remodel & Renovation</h3>
                <p className="text-blue-100">Complete plumbing for kitchen and bathroom remodels. Free estimates.</p>
              </Link>
              <Link to="/commercial-plumbing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Users className="w-12 h-12 text-indigo-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Commercial Plumbing</h3>
                <p className="text-blue-100">Licensed commercial plumbing services for Longmont businesses and properties.</p>
              </Link>
              <Link to="/pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <CheckCircle className="w-12 h-12 text-teal-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Much More</h3>
                <p className="text-blue-100"><Link to="/garbage-disposal-pricing" className="text-blue-200 hover:text-blue-100 underline">Garbage disposals</Link>, <Link to="/faucet-pricing" className="text-blue-200 hover:text-blue-100 underline">faucets</Link>, and all plumbing needs with exact pricing.</p>
              </Link>
              <Link to="/diagnostic-services-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Camera className="w-12 h-12 text-cyan-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Diagnostic Services</h3>
                <p className="text-blue-100">Professional diagnosis $95-$225. Annual checkups $350. Expert evaluation.</p>
              </Link>
            </div>
            
            {/* CTA Section */}
            <div className="mt-12 text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
                <h3 className="text-2xl font-bold mb-4 text-white">Need Affordable Plumbing in Longmont?</h3>
                <p className="text-blue-100 mb-6">
                  Honest pricing • Local Boulder County expertise • Same-day service • Transparent rates online
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  
                  <Link to="/pricing" className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl text-white px-8 py-4 rounded-full font-semibold">
                    💰 See All Pricing
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How Can Others Claim "Affordable" Section */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              How Can Others Claim "Affordable" Without Showing Prices?
            </h2>
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-xl mb-6 text-white font-semibold">
                We are the only Licensed Plumbing Contractor in Colorado proving affordable means transparent. True affordable plumbing shows <Link to="/water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">water heater costs</Link>, <Link to="/tankless-water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">tankless prices</Link>, <Link to="/sewer-repair-replacement" className="text-blue-200 hover:text-blue-100 underline">sewer repair rates</Link>, and more - all online before you call.
              </p>
              <p className="text-lg mb-6 text-blue-100">
                Longmont's tech community appreciates efficiency and transparency. We bring that same approach to plumbing - 
                no salespeople, no high-pressure tactics, just honest pricing and quality work. 
                That's why we're the only plumber showing exact prices online, proving affordable means transparent.
              </p>
              
              {/* Price Comparison Cards */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6">
                  <h3 className="text-green-400 font-bold mb-4">✅ Our Affordable Price: $12,000 (Full Sewer Replacement)</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✓ Exact price online</li>
                    <li>✓ No hidden fees</li>
                    <li>✓ Includes everything</li>
                    <li>✓ Written guarantee</li>
                  </ul>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
                  <h3 className="text-red-400 font-bold mb-4">❌ Others' "Affordable": $???</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✗ "Free estimate"</li>
                    <li>✗ High-pressure sales</li>
                    <li>✗ Price varies by salesperson</li>
                    <li>✗ Hidden upcharges</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Longmont-Specific FAQ Section 1: Sewer Services */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950">
          <div className="container-custom">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Sewer Service Pricing in Longmont
            </h2>
            
            {/* How Much Does a Full Sewer Line Replacement Cost in Longmont? */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-4 text-white">
                How Much Does a Full Sewer Line Replacement Cost in Longmont?
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="text-blue-100 mb-4 leading-relaxed">
                    <strong className="text-yellow-400 text-xl">Colorado First Plumbing and Sewer LLC</strong> offers affordable full sewer line replacement in Longmont for <strong className="text-2xl text-green-400">$12,000</strong> using trenchless pipe bursting technology. This flat-rate price includes the entire trenchless process, providing Longmont homeowners with a modern, long-lasting solution without any pricing surprises.
                  </p>
                  <h4 className="text-lg font-semibold mb-3 text-yellow-300">What Does Your $12,000 Sewer Replacement in Longmont Encompass?</h4>
                  <p className="text-blue-100 mb-4">
                    This all-inclusive price covers the new high-density polyethylene (HDPE) pipe, the trenchless pipe bursting procedure, proper bedding for the new line, professional installation by our master plumbers, and securing all necessary permits and inspections with the City of Longmont. It's a comprehensive package designed for total peace of mind.
                  </p>
                  <p className="text-blue-100">
                    <strong>Colorado First Plumbing and Sewer LLC</strong> specializes in trenchless replacement throughout Longmont's neighborhoods, preserving your established landscaping while delivering a superior sewer line.
                  </p>
                </div>
                <div className="text-center">
                  <img 
                    src="/page-specific-content/longmont-plumber/sewer-root-cleaning.jpg" 
                    alt="Professional sewer line replacement in Longmont using trenchless technology by Colorado First Plumbing - affordable $12,000 flat rate pricing" 
                    className="rounded-lg shadow-lg w-full max-w-md mx-auto" 
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            {/* What is the Cost for a Sewer Spot Repair in a Longmont Street? */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-4 text-white">
                What is the Cost for a Sewer Spot Repair in a Longmont Street?
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="text-center lg:order-2">
                  <img 
                    src="/page-specific-content/longmont-plumber/gas-pipe-inspection-gauge-underground-longmont.jpg" 
                    alt="Professional street sewer spot repair in Longmont with traffic control and restoration - Colorado First Plumbing $9,500 transparent pricing" 
                    className="rounded-lg shadow-lg w-full max-w-md mx-auto" 
                    loading="lazy"
                  />
                </div>
                <div className="lg:order-1">
                  <p className="text-blue-100 mb-4 leading-relaxed">
                    <strong className="text-yellow-400">Colorado First Plumbing and Sewer LLC</strong> provides affordable sewer spot repair in Longmont streets for <strong className="text-2xl text-green-400">$9,500</strong>. This price is exact and all-inclusive, covering the added complexity of working within the public right-of-way, including traffic control and extensive restoration.
                  </p>
                  <h4 className="text-lg font-semibold mb-3 text-yellow-300">Understanding Our $9,500 Flat-Rate for Street-Side Sewer Repairs</h4>
                  <p className="text-blue-100 mb-4">
                    This price accounts for the significant requirements of a street excavation. It includes asphalt or concrete cutting, shoring for safety, the pipe repair itself, proper backfill and compaction, and full restoration of the street surface to meet Longmont's municipal standards. All permitting and traffic management fees are built-in.
                  </p>
                </div>
              </div>
            </div>

            {/* How Do You Determine the Cost to Fix a Sewer Problem in Longmont? */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-4 text-white">
                How Do You Determine the Cost to Fix a Sewer Problem in Longmont?
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="text-blue-100 mb-4 leading-relaxed">
                    <strong className="text-yellow-400">Colorado First Plumbing and Sewer LLC</strong> starts by identifying the exact cause and location of the issue with a professional sewer camera inspection for a flat rate of <strong className="text-xl text-green-400">$175</strong>. Based on the findings, we provide a transparent, fixed-price solution, which could range from a $295 mainline cleaning to a $12,000 full replacement.
                  </p>
                  <h4 className="text-lg font-semibold mb-3 text-yellow-300">The Critical First Step: Our $175 Sewer Camera Inspection</h4>
                  <p className="text-blue-100 mb-4">
                    Our high-definition camera inspection is the most important tool for an accurate diagnosis. For $175, we provide you with a video recording and a detailed report of your sewer line's condition. This eliminates guesswork, ensuring you only pay for the specific service your Longmont home actually needs.
                  </p>
                  <h4 className="text-lg font-semibold mb-3 text-yellow-300">A Clear Path to Your Solution: From Diagnosis to Repair</h4>
                  <p className="text-blue-100">
                    Once we diagnose the issue, we'll recommend the right flat-rate service: Simple clog mainline snaking from a cleanout for $295, grease or roots hydro-jetting for thorough cleaning at $695, localized damage sewer spot repair in the yard for $5,000, or complete failure full trenchless replacement for $12,000.
                  </p>
                </div>
                <div className="text-center">
                  <img 
                    src="/page-specific-content/longmont-plumber/sewer-snake-longmont-spartan-100-drain-cleaner.jpg" 
                    alt="Professional sewer camera inspection in Longmont by Colorado First Plumbing - transparent $175 diagnostic pricing" 
                    className="rounded-lg shadow-lg w-full max-w-md mx-auto" 
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            {/* What Does Specialized Tree Root Cleaning in a Sewer Line Cost in Longmont? */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-4 text-white">
                What Does Specialized Tree Root Cleaning in a Sewer Line Cost in Longmont?
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="text-center lg:order-2">
                  <img 
                    src="/page-specific-content/longmont-plumber/sewer-root-cleaning.jpg" 
                    alt="Professional specialized tree root cleaning in Longmont sewer lines - Colorado First Plumbing affordable $695-$995 pricing" 
                    className="rounded-lg shadow-lg w-full max-w-md mx-auto" 
                    loading="lazy"
                  />
                </div>
                <div className="lg:order-1">
                  <p className="text-blue-100 mb-4 leading-relaxed">
                    <strong className="text-yellow-400">Colorado First Plumbing and Sewer LLC</strong> offers affordable specialized root cleaning in Longmont for <strong className="text-2xl text-green-400">$695</strong> via basement cleanout or <strong className="text-2xl text-green-400">$995</strong> through toilet access. For severe cases, hydro jetting from exterior cleanout is available for $695.
                  </p>
                  <h4 className="text-lg font-semibold mb-3 text-yellow-300">Our $695/$995 Specialized Root Cleaning Service: What's Included?</h4>
                  <p className="text-blue-100 mb-4">
                    This service uses advanced cutting heads designed specifically to shred and remove dense root masses from your sewer line, restoring full flow. The price difference reflects the additional labor involved in removing and resetting a toilet to gain access to the mainline.
                  </p>
                  <h4 className="text-lg font-semibold mb-3 text-yellow-300">A Proactive Solution for Longmont's Mature Landscapes</h4>
                  <p className="text-blue-100">
                    Many Longmont homes are surrounded by beautiful, mature trees whose roots naturally seek out the moisture in sewer lines. Our specialized services are designed to manage this common local issue effectively, providing a long-term solution beyond simple snaking.
                  </p>
                </div>
              </div>
            </div>

            {/* How Much is Sewer Line Descaling for Cast Iron Pipes in Longmont? */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-4 text-white">
                How Much is Sewer Line Descaling for Cast Iron Pipes in Longmont?
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="text-blue-100 mb-4 leading-relaxed">
                    <strong className="text-yellow-400">Colorado First Plumbing and Sewer LLC</strong> offers affordable Milwaukee descaling service for up to 50 feet of mainline in Longmont for a flat rate of <strong className="text-2xl text-green-400">$995</strong>. This is an effective rehabilitation technique for many of Longmont's older homes, preventing the need for a full replacement.
                  </p>
                  <h4 className="text-lg font-semibold mb-3 text-yellow-300">Your $995 Sewer Descaling Service: What It Accomplishes</h4>
                  <p className="text-blue-100 mb-4">
                    This service uses a specialized machine with a chain knocker to safely grind away years of accumulated rust, scale, and corrosion from the inside of your cast iron pipes. This process smooths the pipe interior, improves flow dramatically, and helps prevent future clogs.
                  </p>
                  <h4 className="text-lg font-semibold mb-3 text-yellow-300">Why Descale Older Cast Iron Sewer Pipes in Longmont?</h4>
                  <p className="text-blue-100">
                    Cast iron pipes, prevalent in homes built before the 1980s, are durable but prone to internal scaling that snags waste and causes chronic backups. Descaling is a powerful, non-excavation method to restore the pipe's original function and extend its lifespan, offering significant value over a full replacement.
                  </p>
                </div>
                <div className="text-center">
                  <img 
                    src="/page-specific-content/longmont-plumber/gas-pipe-inspection-gauge-underground-longmont.jpg" 
                    alt="Professional cast iron sewer descaling in Longmont - Colorado First Plumbing affordable $995 Milwaukee descaling service" 
                    className="rounded-lg shadow-lg w-full max-w-md mx-auto" 
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Longmont-Specific FAQ Section 2: Water Heater Services */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container-custom">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Water Heater Service Pricing in Longmont
            </h2>
            
            {/* What is the All-Inclusive Cost for Your 40 Gallon Water Heater Special in Longmont? */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-4 text-white">
                What is the All-Inclusive Cost for Your 40 Gallon Water Heater Special in Longmont?
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="text-blue-100 mb-4 leading-relaxed">
                    <strong className="text-yellow-400">Colorado First Plumbing and Sewer LLC</strong> offers affordable 40-gallon water heater installation in Longmont for <strong className="text-2xl text-green-400">$2,000</strong> during our Summer Special. This is the final, all-inclusive price, covering the unit, all necessary parts for a code-compliant installation, professional labor, and disposal. Our standard price for a premium 40-gallon Bradford White model is $2,425.
                  </p>
                  <h4 className="text-lg font-semibold mb-3 text-yellow-300">What's Included in Our $2,000 Longmont Summer Special?</h4>
                  <p className="text-blue-100">
                    This comprehensive price includes the new 40-gallon atmospheric vented water heater, a thermal expansion tank, a new drain pan, new flexible water supply lines, a new gas drip leg assembly, and professional installation that meets all current plumbing codes. We also handle pulling the permit and haul away your old unit at no extra charge.
                  </p>
                </div>
                <div className="text-center">
                  <img 
                    src="/page-specific-content/longmont-plumber/40-gallon-atmospheric-vent-water-heater-with-thermal-tank-bracket-near-me-longmont.jpg" 
                    alt="Professional 40-gallon water heater installation in Longmont by Colorado First Plumbing - affordable $2,000 summer special pricing" 
                    className="rounded-lg shadow-lg w-full max-w-md mx-auto" 
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            {/* What Does it Cost to Upgrade from a Tank to a Tankless Water Heater in Longmont? */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-4 text-white">
                What Does it Cost to Upgrade from a Tank to a Tankless Water Heater in Longmont?
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="text-center lg:order-2">
                  <img 
                    src="/page-specific-content/longmont-plumber/close-up-propress-fittings-thermal-tank-water-heater.jpg" 
                    alt="Professional tankless water heater conversion in Longmont - Colorado First Plumbing affordable $8,000 complete installation" 
                    className="rounded-lg shadow-lg w-full max-w-md mx-auto" 
                    loading="lazy"
                  />
                </div>
                <div className="lg:order-1">
                  <p className="text-blue-100 mb-4 leading-relaxed">
                    <strong className="text-yellow-400">Colorado First Plumbing and Sewer LLC</strong> offers affordable tank-to-tankless conversion in Longmont for <strong className="text-2xl text-green-400">$8,000</strong>. For homes that already have a tankless unit that needs replacement, our price is $5,000.
                  </p>
                  <h4 className="text-lg font-semibold mb-3 text-yellow-300">Our $8,000 Tank-to-Tankless Conversion: A Complete Package</h4>
                  <p className="text-blue-100 mb-4">
                    This comprehensive price covers the entire conversion process. It includes a new Navien or Rinnai tankless unit, installation of new dedicated venting, necessary upgrades to the gas line to meet the higher demand, professional installation and programming, and all required permits and inspections. Perfect for Longmont's growing tech families who need endless hot water.
                  </p>
                  <h4 className="text-lg font-semibold mb-3 text-yellow-300">Benefits for Longmont Families: Endless Hot Water and Efficiency</h4>
                  <p className="text-blue-100">
                    Upgrading to a tankless system means you'll never run out of hot water again—perfect for busy families. These units are also significantly more energy-efficient, saving you money on utility bills over the long term, and their wall-mounted design frees up valuable floor space in your utility room or basement. Our transparent pricing makes this premium upgrade predictable and stress-free.
                  </p>
                </div>
              </div>
            </div>

            {/* How Much Does a Power Vent Water Heater Installation Cost in Longmont? */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-4 text-white">
                How Much Does a Power Vent Water Heater Installation Cost in Longmont?
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="text-blue-100 mb-4 leading-relaxed">
                    <strong className="text-yellow-400">Colorado First Plumbing and Sewer LLC</strong> offers affordable power-vented water heater installation in Longmont with flat-rate pricing of <strong className="text-2xl text-green-400">$4,450</strong> for a 40-gallon Bradford White model or <strong className="text-2xl text-green-400">$4,850</strong> for a 50-gallon model. These exact prices are all-inclusive, covering the specialized unit and venting requirements.
                  </p>
                  <h4 className="text-lg font-semibold mb-3 text-yellow-300">Understanding Your $4,450 / $4,850 Power Vent Installation</h4>
                  <p className="text-blue-100 mb-4">
                    This price includes the premium Bradford White power vent unit, all PVC venting materials, professional installation to ensure proper and safe exhaust, and all parts needed for a code-compliant setup (expansion tank, pan, etc.). Power vent models are necessary when a traditional chimney isn't available, and our price covers this complexity.
                  </p>
                  <h4 className="text-lg font-semibold mb-3 text-yellow-300">When Does a Longmont Home Need a Power Vent Water Heater?</h4>
                  <p className="text-blue-100">
                    Your home likely needs a power vent water heater if your unit is located in a finished basement, a utility closet without a direct vertical vent stack, or anywhere that requires the exhaust to be routed horizontally through a wall to the outdoors. This is a common requirement in many modern home layouts.
                  </p>
                </div>
                <div className="text-center">
                  <img 
                    src="/page-specific-content/longmont-plumber/powervented-bradford-white-water-heater-flat-rate.jpg" 
                    alt="Professional power vent water heater installation in Longmont - Colorado First Plumbing affordable $4,450-$4,850 pricing" 
                    className="rounded-lg shadow-lg w-full max-w-md mx-auto" 
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Longmont-Specific FAQ Section 3: Plumbing System Information */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950">
          <div className="container-custom">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Plumbing System Information for Longmont Homeowners
            </h2>
            
            {/* My Drains Are Slow in My Longmont Home. What Should I Do First? */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 mb-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-4 text-white">
                My Drains Are Slow in My Longmont Home. What Should I Do First?
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="text-blue-100 mb-4 leading-relaxed">
                    The first and most effective step is to schedule a professional sewer camera inspection from <strong className="text-yellow-400">Colorado First Plumbing and Sewer LLC</strong> for a flat rate of <strong className="text-xl text-green-400">$175</strong>. This diagnostic service allows us to see exactly what is causing the slow drains—be it grease, roots, or a pipe issue—and recommend the correct, transparently-priced solution without any guesswork.
                  </p>
                  <h4 className="text-lg font-semibold mb-3 text-yellow-300">Why Longmont Homeowners Choose Professional Diagnosis</h4>
                  <p className="text-blue-100">
                    This avoids wasting money on temporary fixes or harmful chemical drain cleaners. Our Longmont customers appreciate the tech-forward approach of seeing the actual problem before paying for repairs. Located in Boulder County's innovation corridor, Longmont residents understand the value of data-driven solutions over guesswork.
                  </p>
                </div>
                <div className="text-center">
                  <img 
                    src="/page-specific-content/longmont-plumber/Remodel-longmont-drain-pvc-basement (1).jpg" 
                    alt="Professional drain diagnosis in Longmont home - Colorado First Plumbing camera inspection to identify slow drain causes" 
                    className="rounded-lg shadow-lg w-full max-w-md mx-auto" 
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            {/* What Areas of Longmont Do You Service for Emergency Plumbing? */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <h3 className="text-2xl font-bold mb-4 text-white">
                What Areas of Longmont Do You Service for Emergency Plumbing?
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="text-center lg:order-2">
                  <img 
                    src="/page-specific-content/longmont-plumber/powervented-bradford-white-water-heater-flat-rate.jpg" 
                    alt="Emergency plumbing service areas in Longmont Colorado - Colorado First Plumbing serving all Longmont neighborhoods" 
                    className="rounded-lg shadow-lg w-full max-w-md mx-auto" 
                    loading="lazy"
                  />
                </div>
                <div className="lg:order-1">
                  <p className="text-blue-100 mb-4 leading-relaxed">
                    <strong className="text-yellow-400">Colorado First Plumbing and Sewer LLC</strong> provides affordable emergency plumbing services throughout all Longmont areas including Downtown Longmont (Main Street District, St. Vrain Historic District), East Longmont (Eastridge, Cambridge Heights, Twin Peaks, Village at the Peaks), West Longmont (Sunset, Westview, Quail Campus, Prospect New Town), and South Longmont (Fox Hill, Clover Basin, Spring Gulch, The Meadows).
                  </p>
                  <h4 className="text-lg font-semibold mb-3 text-yellow-300">Complete Longmont Coverage with Transparent Pricing</h4>
                  <p className="text-blue-100">
                    Emergency service available during business hours (8 AM - 5 PM Mon-Sat) throughout all of Longmont. This includes priority scheduling and rapid response, plus repairs at our regular transparent rates. No hidden fees or surprise charges - even our tech hub customers in the Quail Campus area appreciate knowing exactly what service costs upfront.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Summary CTA Section */}
        <section className="py-16 bg-gradient-to-r from-action-primary to-action-urgent">
          <div className="container-custom text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Ready for Affordable, Transparent Plumbing in Longmont?
            </h2>
            <p className="text-xl mb-8 text-orange-100 max-w-3xl mx-auto">
              Join 1000+ satisfied customers across all platforms who chose <strong>Colorado First Plumbing and Sewer LLC</strong> for honest pricing and quality work. 
              See why we're Longmont's most trusted affordable plumber.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              
              <Link 
                to="/pricing" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-5 rounded-full text-xl font-semibold transition-all duration-300 shadow-xl hover:scale-105"
              >
                💰 View All Affordable Prices
              </Link>
            </div>
            <div className="text-orange-100">
              <p className="mb-2"><strong>Licensed Master Plumber</strong> • <Link to="/team/michelle-jessen" className="text-yellow-200 hover:text-yellow-100 underline">Woman Majority Owned</Link> • <strong>18,000+ Jobs Completed</strong></p>
              <p><strong>A+ BBB Rating</strong> • <strong>1000+ Reviews Across All Platforms</strong> • <strong>Serving Longmont Since 2018</strong></p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default LongmontPlumber;